import mapboxgl from "mapbox-gl";
import buildPopupHTMLContent from "./buildPopupHTMLContent";

const MAPBOX_POPUP_CLASS_NAME = "mapboxgl-popup";
const INDIVIDUAL_CONFLICT_POPUP_CLASS_NAME = "persisting-conflict-popup";
const PERSISTING_POI_POPUP_CLASS_NAME = "persisting-poi-popup";

let poiPopupInstance = null;
let isMouseOverPopup = false;

export const removeOldPopups = (click) => {
  const oldPopups = document.getElementsByClassName(MAPBOX_POPUP_CLASS_NAME);
  if (oldPopups.length) {
    for (let i = oldPopups.length - 1; i >= 0; i--) {
      const popup = oldPopups[i];
      if (
        click ||
        (!popup.className.includes(INDIVIDUAL_CONFLICT_POPUP_CLASS_NAME) &&
          !popup.className.includes(PERSISTING_POI_POPUP_CLASS_NAME))
      ) {
        popup.remove();
      }
    }
  }
};

const createPOIPopupHTML = (label, description, link) => `
  <div class="poi-popup-content" style="max-width: 300px;">
    <h3 style="margin-top: 0; margin-bottom: 8px; font-size: 16px; font-weight: bold;">${label}</h3>
    <p style="margin-bottom: 8px; font-size: 14px;">${description}</p>
    <a href="${link}" target="_blank" rel="noopener noreferrer" style="color: #0066cc; text-decoration: underline; font-size: 14px;">More information</a>
  </div>
`;

const setupPOIPopupBehavior = (popup) => {
  // Store reference to current popup
  poiPopupInstance = popup;

  // Get the popup DOM element
  const popupElement = popup.getElement();

  // Handle mouse entering the popup
  const handleMouseEnter = () => {
    isMouseOverPopup = true;
  };

  // Handle mouse leaving the popup
  const handleMouseLeave = () => {
    isMouseOverPopup = false;
    popup.remove();
    poiPopupInstance = null;
  };

  // Add event listeners
  popupElement.addEventListener("mouseenter", handleMouseEnter);
  popupElement.addEventListener("mouseleave", handleMouseLeave);
};

const addMapListeners = (
  global,
  map,
  activeLayerName,
  activeFeatures,
  hasVisibleProduct,
  translateFunc,
  onFeatureClick,
  activeConflictData,
  pointsofinterest_active
) => {
  if (global.mapOnMousemove) {
    map.off("mousemove", global.mapOnMousemove);
    global.mapOnMousemove = null;
  }

  let popup = new mapboxgl.Popup({
    closeButton: false,
    anchor: "bottom",
    offset: 5,
  });

  const getFeatures = (e) => {
    const layers = [activeLayerName + "-fill"];

    // Add active Conflict layers
    if (map.getLayer("conflict-layer")) {
      layers.push("conflict-layer");
    }
    // Add active POI layers
    const activePOIs = pointsofinterest_active || [];
    activePOIs.forEach((poiType) => {
      const poiLayerName = `${poiType}-poi-layer`;
      if (map.getLayer(poiLayerName)) {
        layers.push(poiLayerName);
      }
    });

    return map.queryRenderedFeatures(e.point, { layers });
  };

  const getFeatureFills = (features) =>
    features.filter((f) => f.layer.id === activeLayerName + "-fill");

  global.mapOnMousemove = function (e) {
    try {
      var features = getFeatures(e);
    } catch (error) {
      return;
    }

    if (features.length > 0) {
      const fills = getFeatureFills(features);
      const conflicts = features.filter((f) => f.layer.id === "conflict-layer");
      const poiFeatures = features.filter((f) =>
        f.layer.id.endsWith("-poi-layer")
      );
      const feature = fills[0];
      const activeLayerFeatures = activeFeatures[activeLayerName] ?? [];
      map.getCanvas().style.cursor = "pointer";

      // Handle POI popups separately because they contain clickable links.
      // Popup remains visible while hovering over either the POI marker or
      // the popup itself, allowing users to interact with the links.
      if (poiFeatures.length > 0) {
        const poiFeature = poiFeatures[0];
        const { label, description, link } = poiFeature.properties;

        // Only create a new popup if one isn't already active
        if (poiPopupInstance && poiPopupInstance.isOpen()) {
          return;
        }

        const content = createPOIPopupHTML(label, description, link);

        // Remove non-persisting popups only
        removeOldPopups();

        // Create new POI popup
        const poiPopup = new mapboxgl.Popup({
          closeButton: false,
          anchor: "bottom",
          offset: 5,
        });

        poiPopup.setLngLat(e.lngLat).setHTML(content).addTo(map);

        // Mark as POI popup immediately to protect from removal
        poiPopup.addClassName(PERSISTING_POI_POPUP_CLASS_NAME);

        // Set up the interactive behavior
        setupPOIPopupBehavior(poiPopup);
        return;
      }
      if (feature) {
        [...activeLayerFeatures, global.feature_id_hovering].forEach(
          (other_id) => {
            map.setFeatureState(
              {
                source: "features",
                sourceLayer: activeLayerName,
                id: other_id,
              },
              { hover: false }
            );
            map.setFeatureState(
              {
                source: "features",
                sourceLayer: activeLayerName + "-labels",
                id: other_id,
              },
              { hover: false }
            );
          }
        );
        if (feature.id) {
          map.setFeatureState(
            {
              source: "features",
              sourceLayer: activeLayerName,
              id: feature.id,
            },
            { hover: true }
          );
          map.setFeatureState(
            {
              source: "features",
              sourceLayer: activeLayerName + "-labels",
              id: feature.id,
            },
            { hover: true }
          );
          global.feature_id_hovering = feature.id;
        }
        if (feature.properties) {
          const content = buildPopupHTMLContent(
            feature,
            conflicts,
            translateFunc,
            activeConflictData
          );
          let ll = e.lngLat;
          if (hasVisibleProduct) {
            removeOldPopups();
            // Don't show individual conflict on hover, only click
            if (
              !conflicts.length ||
              (conflicts.length && conflicts[0].properties.cluster)
            ) {
              popup.setLngLat(ll).setHTML(content).addTo(map);
            }
          }
        }
      }
    } else {
      map.getCanvas().style.cursor = "";
      if (global.feature_id) {
        map.setFeatureState(
          {
            source: "features",
            sourceLayer: activeLayerName,
            id: global.feature_id,
          },
          { hover: false }
        );
        global.feature_id = null;
      }
      popup.remove();

      // Close POI popup if mouse has left both the marker and popup
      if (poiPopupInstance && poiPopupInstance.isOpen() && !isMouseOverPopup) {
        poiPopupInstance.remove();
        poiPopupInstance = null;
      }
    }
  };
  map.on("mousemove", global.mapOnMousemove);

  // Unregister any click listeners
  // This used to be done more idiomatically with map.off(...),
  // storing function references as a global, but those stopped
  // matching correctly, so we're just hacking into the map now.
  map._listeners.click = [];

  // Open graphs when clicking region of map
  const mapOnClick = function (e) {
    if (e.features.length > 0) {
      const fills = getFeatureFills(e.features);
      const feature = fills[0];

      onFeatureClick(feature);
    }
  };
  map.on("click", activeLayerName + "-fill", mapOnClick);

  map.on("mouseout", () => {
    removeOldPopups();
  });

  // Open/Close individual conflict popup on click
  map.on("click", (e) => {
    const activeConflictPopup = document.getElementsByClassName(
      INDIVIDUAL_CONFLICT_POPUP_CLASS_NAME
    );
    if (activeConflictPopup.length) {
      activeConflictPopup[0].remove();
    }
    const features = getFeatures(e);
    const conflicts = features.filter((f) => f.layer.id === "conflict-layer");
    const fills = getFeatureFills(features);
    const feature = fills[0];

    if (
      hasVisibleProduct &&
      feature?.properties &&
      conflicts.length &&
      !conflicts[0].properties.cluster
    ) {
      const content = buildPopupHTMLContent(
        feature,
        conflicts,
        translateFunc,
        activeConflictData
      );
      let ll = e.lngLat;
      popup.setLngLat(ll).setHTML(content).addTo(map);
      popup.addClassName(INDIVIDUAL_CONFLICT_POPUP_CLASS_NAME);
    }
  });
};

export default addMapListeners;
